import type { ReactNode } from 'react';
import type { ClientAccountCard, ClientContact } from '../types';
import { Avatar, Badge, Card, EmptyState, MicroLabel, SectionHead, cn } from '../ui/primitives';

/**
 * Who at the client we deal with, and where each of them stands.
 *
 * ── Why this one is a table and the other sections are not ──
 *
 * Quotes, orders and products are lists of DOCUMENTS: each row has a title that
 * carries it, and the rest is supporting detail. A contact has no title — it is
 * a person with a handful of small attributes, several of them often blank.
 *
 * Rendered as free-form rows those attributes have to be self-describing, and
 * they are not: a phone number reads on its own, a date does not, and a row
 * missing both leaves a hole where the next row has content. Headers fix
 * exactly that — they say what a bare value is, they let columns line up down
 * the page, and they make an empty cell read as "we don't have this" rather
 * than as a layout fault.
 *
 * ── Empty columns are dropped ──
 *
 * A column no row can fill is noise, so if nobody has a birthday recorded the
 * header goes too rather than ruling off an empty strip.
 */
export function ClientContacts({
	contacts,
	account,
	meta,
	action,
	emptyHint,
	emptyAction,
	renderContactAction,
}: {
	contacts: ClientContact[];
	/** The card beside the table. Omitted entirely when the app has nothing for
	 * it, rather than rendering an empty panel. */
	account?: ClientAccountCard | null;
	meta?: ReactNode;
	action?: ReactNode;
	emptyHint?: string;
	emptyAction?: ReactNode;
	renderContactAction?: (contact: ClientContact) => ReactNode;
}) {
	const showPhone = contacts.some((c) => c.phone);
	const showBirthday = contacts.some((c) => c.birthdayLabel);
	const showJoined = contacts.some((c) => c.joinedLabel);
	const showStatus = contacts.some((c) => c.status);

	return (
		<>
			<SectionHead title="Contacts & access" meta={meta} action={action} />
			<div
				className={cn(
					'grid items-start gap-3.5',
					account && 'xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]'
				)}
			>
				{contacts.length === 0 ? (
					<EmptyState
						title="No contacts yet"
						hint={
							emptyHint ??
							'Invite someone to give this client a seat in their own portal.'
						}
						action={emptyAction}
					/>
				) : (
					<Card>
						{/* Scrolls inside its own card rather than pushing the page
						    sideways on a narrow viewport. */}
						<div className="overflow-x-auto">
							<table className="w-full border-collapse text-left">
								<thead>
									<tr className="border-b border-line-1">
										<th className="px-4.5 py-2.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-text-4">
											Contact
										</th>
										{showPhone && (
											<th className="whitespace-nowrap px-3 py-2.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Phone
											</th>
										)}
										{showBirthday && (
											<th className="whitespace-nowrap px-3 py-2.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Birthday
											</th>
										)}
										{showJoined && (
											<th className="whitespace-nowrap px-3 py-2.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Joined
											</th>
										)}
										{showStatus && (
											<th className="whitespace-nowrap px-3 py-2.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Status
											</th>
										)}
										{renderContactAction && <th className="w-10 px-3 py-2.5" />}
									</tr>
								</thead>
								<tbody>
									{contacts.map((contact, i) => (
										<tr
											key={contact.id}
											className={
												i < contacts.length - 1
													? 'border-b border-line-1'
													: undefined
											}
										>
											<td className="px-4.5 py-3">
												<div className="flex items-center gap-3">
													<Avatar
														name={contact.name}
														size={30}
														className="rounded-full"
													/>
													<div className="min-w-0">
														<div className="truncate text-base font-medium text-text-1">
															{contact.name}
														</div>
														<div className="mt-0.5 truncate text-xs text-text-4">
															{contact.role}
															{contact.role && contact.email
																? ' · '
																: ''}
															{contact.email && (
																<span className="font-mono">
																	{contact.email}
																</span>
															)}
														</div>
													</div>
												</div>
											</td>

											{showPhone && (
												<td className="whitespace-nowrap px-3 py-3 font-mono text-base text-text-3">
													{contact.phone || (
														<span className="text-text-4">—</span>
													)}
												</td>
											)}

											{showBirthday && (
												<td className="whitespace-nowrap px-3 py-3 text-base text-text-3">
													{contact.birthdayLabel || (
														<span className="text-text-4">—</span>
													)}
												</td>
											)}

											{showJoined && (
												<td className="whitespace-nowrap px-3 py-3 text-base text-text-3">
													{contact.joinedLabel || (
														<span className="text-text-4">—</span>
													)}
												</td>
											)}

											{showStatus && (
												<td className="whitespace-nowrap px-3 py-3">
													{contact.status && (
														<Badge tone={contact.status.tone} dot>
															{contact.status.label}
														</Badge>
													)}
												</td>
											)}

											{renderContactAction && (
												<td className="px-3 py-3">
													<div className="flex justify-end">
														{renderContactAction(contact)}
													</div>
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</Card>
				)}

				{account && (
					<Card padded>
						<MicroLabel className="mb-3 block">Account &amp; shipping</MicroLabel>

						{account.ownerName && (
							<div className="mb-3.5 flex items-center gap-2.5">
								<Avatar
									name={account.ownerName}
									size={30}
									className="rounded-full"
								/>
								<div className="min-w-0">
									<div className="truncate text-base font-medium text-text-1">
										{account.ownerName}
									</div>
									{account.ownerRole && (
										<div className="mt-0.5 truncate text-xs text-text-4">
											{account.ownerRole}
										</div>
									)}
								</div>
							</div>
						)}

						{account.addressLines && account.addressLines.length > 0 && (
							<div className="border-t border-line-1 pt-3 text-xs leading-relaxed text-text-1">
								{account.addressLines.map((line, i) => (
									<div
										key={`${line}-${i}`}
										className={i > 0 ? 'text-text-3' : undefined}
									>
										{line}
									</div>
								))}
							</div>
						)}

						{account.billingLabel && (
							<div className="mt-3 flex justify-between border-t border-line-1 pt-3 text-2xs">
								<span className="text-text-4">Billing</span>
								<span className="text-text-2">{account.billingLabel}</span>
							</div>
						)}
					</Card>
				)}
			</div>
		</>
	);
}
